import { ChevronDown, ChevronUp, Pencil, BarChart3, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { TIER_CONFIG } from './TierConfig';
import { apiClient } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export function MemberRow({ member, isExpanded, onToggle, onEditMember, onEditMetrics, onDelete, onRefresh, onViewHistory }) {
  const m = member.metrics;
  return (
    <div data-testid={`member-${member.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#0f0f17] transition-colors"
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
          member.role === 'admin' ? 'bg-[#FF6B35]/20 text-[#FF6B35]' : 'bg-zinc-800 text-zinc-400'
        }`}>
          {member.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white font-medium text-sm truncate">{member.name}</p>
            {member.role === 'admin' && (
              <span className="text-[10px] px-1.5 py-0.5 bg-[#FF6B35]/20 text-[#FF6B35] rounded-sm">COACH</span>
            )}
            {member.tier && TIER_CONFIG[member.tier] && (() => {
              const t = TIER_CONFIG[member.tier];
              const TierIcon = t.icon;
              return (
                <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm flex items-center gap-0.5 ${t.bg} ${t.color}`} data-testid={`tier-badge-${member.id}`}>
                  <TierIcon className="w-2.5 h-2.5" /> {t.label}
                </span>
              );
            })()}
          </div>
          <p className="text-xs text-zinc-400">
            {m ? `${m.weight || '-'}kg · ${m.body_fat ?? '-'}% MG · ${m.muscle_mass ?? '-'}kg muscle` : 'Pas de metriques'}
          </p>
        </div>
        <div className="text-right mr-2">
          <p className="font-mono text-sm text-[#FF6B35]">{member.points} pts</p>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 bg-[#0F0F0F] border-t border-[#2A2A2E]">
          {m && (
            <div className="grid grid-cols-4 gap-2 pt-3">
              {[
                { label: 'Poids', value: `${m.weight} kg` },
                { label: 'Graisse', value: `${m.body_fat} kg` },
                { label: 'Muscle', value: `${m.muscle_mass} kg` },
                { label: 'Age meta.', value: m.metabolic_age },
              ].map(item => (
                <div key={item.label} className="text-center p-2 bg-[#1B1B1B] rounded-sm">
                  <p className="text-xs text-zinc-400">{item.label}</p>
                  <p className="font-mono text-sm font-bold text-white">{item.value}</p>
                </div>
              ))}
            </div>
          )}
          <div className="text-xs text-zinc-500 space-y-1">
            <p>ID: <span className="font-mono text-zinc-400">{member.id}</span></p>
            <p>Badges: {member.badges?.length > 0 ? member.badges.join(', ') : 'Aucun'}</p>
            <p>Inscrit: {member.created_at ? new Date(member.created_at).toLocaleDateString('fr-FR') : '-'}</p>
          </div>
          <div className="pt-2">
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1.5">Badge Meute</p>
            <div className="flex gap-2">
              {['alpha', 'beta', 'omega'].map(tierKey => {
                const t = TIER_CONFIG[tierKey];
                const TIcon = t.icon;
                const isActive = member.tier === tierKey;
                return (
                  <button
                    key={tierKey}
                    onClick={async () => {
                      try {
                        const newTier = isActive ? '' : tierKey;
                        await apiClient.put(`/admin/members/${member.id}`, { tier: newTier });
                        toast.success(`${member.name} → ${newTier ? t.label : 'Aucun badge'}`);
                        onRefresh();
                      } catch { toast.error('Erreur'); }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-sm text-xs transition-all ${isActive ? `${t.bg} ${t.color} font-bold` : 'border-[#2A2A2E] text-zinc-500 hover:border-zinc-600'}`}
                    data-testid={`set-tier-${tierKey}-${member.id}`}
                  >
                    <TIcon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" onClick={onEditMember} data-testid={`edit-member-${member.id}`}>
              <Pencil className="w-3 h-3 mr-1" /> Modifier profil
            </Button>
            <Button size="sm" className="flex-1 bg-blue-600 text-white hover:bg-blue-700" onClick={onEditMetrics} data-testid={`edit-metrics-${member.id}`}>
              <BarChart3 className="w-3 h-3 mr-1" /> Metriques
            </Button>
            <Button size="sm" className="bg-[#FFB800] text-black hover:bg-[#FFD000]" onClick={onViewHistory} data-testid={`view-history-${member.id}`}>
              <BarChart3 className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" onClick={onDelete} data-testid={`delete-member-${member.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
